import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useThemeMode } from "../../context/ThemeContext";

function getCssVar(name, fallback) {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

export function BarChart({
  labels = [],
  values = [],
  xTitle = "",
  yTitle = "",
  height = 300,
  xAxisLabelRotate = 0,
  xAxisLabelInterval = "auto",
  yAxisMinInterval,
}) {
  const { mode } = useThemeMode();

  const option = useMemo(() => {
    const primary = getCssVar("--color-primary", "#2563eb");
    const textColor = getCssVar("--color-text-muted", "#64748b");
    const borderColor = getCssVar("--color-border", "#e5e9f2");

    return {
      darkMode: mode === "black",
      animationDuration: 500,

      grid: {
        top: 50,
        right: 20,
        bottom: xAxisLabelRotate ? 88 : 70,
        left: 60,
      },

      tooltip: {
        trigger: "axis",
        backgroundColor: "#111827",
        borderWidth: 0,
        textStyle: { color: "#fff" },
      },

      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: true,

        name: xTitle,
        nameLocation: "middle",
        nameGap: 45,
        nameTextStyle: {
          color: textColor,
          fontWeight: 600,
          fontSize: 13,
        },

        axisTick: { show: false },

        axisLine: {
          lineStyle: { color: borderColor },
        },

        axisLabel: {
          color: textColor,
          fontWeight: 600,
          rotate: xAxisLabelRotate,
          interval: xAxisLabelInterval,
        },
      },

      yAxis: {
        type: "value",

        name: yTitle,
        nameLocation: "middle",
        nameGap: 50,
        nameRotate: 90,
        nameTextStyle: {
          color: textColor,
          fontWeight: 600,
          fontSize: 13,
        },

        splitLine: {
          lineStyle: {
            color: borderColor,
            type: "dashed",
          },
        },

        axisLabel: {
          color: textColor,
          fontWeight: 600,
        },
        minInterval: yAxisMinInterval,
      },

      series: [
        {
          type: "bar",
          data: values,

          barMaxWidth: 32,
          barCategoryGap: "35%",

          itemStyle: {
            color: primary,
            borderRadius: [6, 6, 2, 2],
          },

          emphasis: {
            itemStyle: {
              color: primary,
            },
          },
        },
      ],
    };
  }, [
    labels,
    values,
    mode,
    xTitle,
    yTitle,
    xAxisLabelRotate,
    xAxisLabelInterval,
    yAxisMinInterval,
  ]);

  return (
    <ReactECharts option={option} style={{ height, width: "100%" }} />
  );
}

export default BarChart;
